import React from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// All logos shown in real colors on a white strip, equal visual weight.
const LOGOS = [
  {
    name: "Touchpoints",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_work-portfolio-239/artifacts/eua49uvj_Touchpoints%20Logo.webp",
    // Artboard has heavy padding — visually up-scale to match the rest
    scale: 1.7,
  },
  {
    name: "Nota",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_work-portfolio-239/artifacts/8g2vjoyz_Nota%20logo.png",
    // Nota is drawn with a very light outline — force to ink so it reads on white
    filter: "brightness(0) saturate(100%)",
  },
  {
    name: "Collab",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_work-portfolio-239/artifacts/snz4c2v6_logo%20open%20file%20%28dark%20green%29%202%20copy.png",
  },
  {
    name: "Squircle",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_work-portfolio-239/artifacts/i6mpjx4x_Squircle%20Logo%20Final-01.webp",
  },
  {
    name: "sde",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_work-portfolio-239/artifacts/o06mz18l_sde%20logo%202.png",
  },
];

export default function Portfolio() {
  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="shell pt-10 md:pt-14 pb-20 md:pb-28"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="border-b border-[color:var(--collab-hairline)] pb-6 text-center"
      >
        <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] balance mx-auto">
          Our Collective Muscle
        </h2>
      </motion.div>

      {/* Client logos — no strip, transparent on cream */}
      <div className="mt-12 md:mt-16">
        <div className="grid grid-cols-2 md:grid-cols-5 items-center">
          {LOGOS.map((logo, i) => (
            <motion.div
              key={logo.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="flex items-center justify-center py-0 md:py-1 px-2 md:px-4"
              data-testid={`portfolio-logo-${i}`}
              aria-label={logo.name}
            >
              <img
                src={logo.src}
                alt={logo.name}
                // Equal visual weight: same cap height for all, contain within cell
                className="h-36 md:h-52 w-auto max-w-full object-contain"
                style={{
                  filter: logo.filter || undefined,
                  transform: logo.scale ? `scale(${logo.scale})` : undefined,
                  transformOrigin: "center",
                  // Blend baked-in white backgrounds into the cream page
                  mixBlendMode:
                    logo.name === "Collab" || logo.name === "Touchpoints"
                      ? "multiply"
                      : "normal",
                }}
                loading="lazy"
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Positioning paragraph below the logo strip */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mt-4 md:mt-6 max-w-none mx-auto text-center"
      >
        <p className="font-display font-bold text-[1.8rem] md:text-[2.25rem] lg:text-[2.7rem] leading-[1.25] tracking-[-0.02em] text-[color:var(--collab-forest)] whitespace-nowrap">
          Collab is not just one agency — it's a collective.
        </p>
        <p className="mt-6 md:mt-8 font-display font-light text-2xl md:text-3xl lg:text-4xl leading-[1.3] tracking-[-0.01em] text-[color:var(--collab-ink)] balance">
          We bring layered capabilities across brand consulting, tech,
          design, and content production. This makes us agile, deep-rooted,
          and able to operate at any scale.
        </p>
      </motion.div>
    </section>
  );
}
